import { useQuery, useReactiveVar } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Banner, BottomButtonsLayout, Button } from 'aimo-ui';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import Icon from 'components/icons/Icon';
import { TitleCard } from 'components/longTermParking';
import PaymentCardDropDown from 'components/park/detail/PaymentCardDropDown';
import VehicleSelectDropDown from 'components/park/detail/VehicleSelectDropDown';
import { FooterButtonContainer } from 'components/sheets/SheetLayout';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { useAttachmentStore } from 'stores/attachmentStore';
import { READ_LANDLORD } from 'graphql/queries/readLandlord';
import useCreateSalesOrder from 'hooks/useCreateSalesOrder';
import { usePatchOrderAttachmentLinks } from 'hooks/usePatchOrderAttachmentLinks';
import usePermitPurchaseInfo from 'hooks/usePermitPurchaseInfo';
import usePmcPromotion from 'hooks/usePmcPromotion';
import { useSalesOrderAttachmentScreen } from 'hooks/useSalesOrderAttachmentScreen';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PermitBillingTypeInfoBox } from 'screens/permit/PermitInfoBoxes';
import { confirmCreateSalesOrderData } from 'states/common.ts';
import { useAuthStore } from 'stores/authStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  CountryCode,
  Pmc,
  Query,
  QueryReadLandlordArgs,
} from 'types/generatedSchemaTypes.ts';
import { RootStackParamList } from 'types/navigation.ts';
import {
  isRequiredSalesOrderAttachmentMissing,
  resolveAttachmentUi,
} from 'utils/attachmentUtils';
import {
  getMidnightUTCFromISOString,
  isSalesOrderFree,
} from 'utils/commonUtils.ts';
import { isWeb } from 'utils/deviceUtils';
import { parseFeatureFlagObject, TypeDictionary } from 'utils/featureFlagUtils';
import { determinePaymentType } from 'utils/paymentUtils.ts';
import { getBillingType, showLicensePlate } from 'utils/permitUtils.ts';
import { testIdentifiers } from 'utils/testIdentifiers.ts';
import { useUiStore } from 'stores/uiStore';

const WarningIcon = () => <Icon name="Info-Outlined" />;

const getPlaceType = (
  pmc?: Pmc
): 'PARKING_ZONE' | 'SALES_LOCATION' | undefined => {
  if (pmc?.physicalZoneUid) {
    return 'PARKING_ZONE';
  } else if (pmc?.salesLocationUid) {
    return 'SALES_LOCATION';
  }
};

const BuyScreen: React.FC = () => {
  const { licensePlate } = useSelectedParkingVehicle() || {};
  const paymentMethod = useSelectedPaymentMethod();
  const creditCardId = paymentMethod?.cardId;
  const isMobilePaySelected = paymentMethod?.method === 'MOBILEPAY';
  const { navigate, setOptions } =
    useNavigation<StackNavigationProp<RootStackParamList>>();

  const {
    selectedPmc,
    price,
    placeName,
    startDate,
    selectedBillingFrequency,
    params,
  } = usePermitPurchaseInfo();
  const setSelectedPermitStartDate = usePermitStore(
    (state) => state.setSelectedPermitStartDate
  );

  const isUserRestricted = useAuthStore((state) => state.accountRestricted);
  const parkingSpace = useParkzoneStore((state) => state.selectedParkingSpace);
  const permitData = useReactiveVar(confirmCreateSalesOrderData);
  const unlockCode = permitData?.unlockCode;
  const selectedLicensePlates = usePermitStore(
    (state) => state.selectedLicensePlatesPermitRedeem
  );

  const { t } = useTranslation();

  const { data: landlordData } = useQuery<Query, QueryReadLandlordArgs>(
    READ_LANDLORD,
    {
      variables: {
        input: {
          vendorId: selectedPmc?.vendorId ?? null,
          placeUid:
            selectedPmc?.physicalZoneUid ?? selectedPmc?.salesLocationUid,
          placeType: getPlaceType(selectedPmc),
        },
      },
      fetchPolicy: 'cache-and-network',
      skip:
        !selectedPmc ||
        (!selectedPmc.forResidentsOnly && !selectedPmc.vendorId),
    }
  );

  const landlordName = landlordData?.readLandlord?.name ?? null;
  const {
    reservationType,
    name,
    prices,
    parkZone,
    countryCode = 'FI',
    billingTypes,
    onlineBookingFlexibleStartDate,
    startSellingDate,
    maxLicensePlatesPerQuantity,
    automaticOnlineOrderActivation,
  } = selectedPmc ?? {};

  const {
    flags: { permit_start_date },
  } = useContext(FeatureFlagsContext);

  const allowStarDateChangeCountries = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(permit_start_date, 'permit_start_date');

  const { createDraftSalesOrder, loadingSalesOrder } = useCreateSalesOrder();
  const { addCard, loadingAddCard } = useAddPaymentMethod();

  const freeSalesOrder = isSalesOrderFree(prices, startDate);

  const parkingSpaceNotSelected = !parkingSpace?.parkingSpaceUid;

  const billingType = getBillingType(billingTypes);

  const { showOnSalesOrder, requireOnSalesOrder } = resolveAttachmentUi(
    selectedPmc?.attachmentPolicy
  );
  const pmcId = selectedPmc?.pmcId;
  const salesOrderId = permitData?.salesOrderId;

  const { promotion, loading: promotionLoading } = usePmcPromotion(
    pmcId ?? undefined,
    determinePaymentType(paymentMethod)
  );

  const eagerDraftFired = useRef(false);
  const eagerDraftPayloadRef = useRef({
    selectedPmc,
    startDate,
    billingType,
    unlockCode,
    automaticOnlineOrderActivation,
    createDraftSalesOrder,
  });
  eagerDraftPayloadRef.current = {
    selectedPmc,
    startDate,
    billingType,
    unlockCode,
    automaticOnlineOrderActivation,
    createDraftSalesOrder,
  };

  useEffect(() => {
    if (!showOnSalesOrder || !pmcId || salesOrderId) return;
    if (eagerDraftFired.current) return;
    eagerDraftFired.current = true;
    const p = eagerDraftPayloadRef.current;
    p.createDraftSalesOrder({
      pmcId,
      selectedPmc: p.selectedPmc,
      startDate: new Date(
        getMidnightUTCFromISOString(p.startDate.toISOString())
      ),
      billingType: p.billingType,
      unlockCode: p.unlockCode,
      automaticOnlineOrderActivation: p.automaticOnlineOrderActivation ?? false,
      skipModal: true,
      prices: p.selectedPmc?.prices ?? [],
      countryCode: p.selectedPmc?.countryCode,
    }).catch(() => undefined);
  }, [showOnSalesOrder, pmcId, salesOrderId]);

  const onAttachmentChanged = usePatchOrderAttachmentLinks();

  useSalesOrderAttachmentScreen({
    orderUid: salesOrderId,
    orderAttachments: undefined,
    enableSalesOrderAttachments: showOnSalesOrder,
    onAttachmentChanged,
  });

  const liveAttachments = useAttachmentStore((state) => state.attachments);
  const hasInFlightUpload = useMemo(
    () =>
      !!salesOrderId &&
      liveAttachments.some((a) => a.entityId === salesOrderId && !a.uploadUid),
    [liveAttachments, salesOrderId]
  );
  const uploadedAttachmentCount = useMemo(
    () =>
      salesOrderId
        ? liveAttachments.filter(
            (a) => a.entityId === salesOrderId && !!a.uploadUid
          ).length
        : 0,
    [liveAttachments, salesOrderId]
  );
  const requiredAttachmentMissing = isRequiredSalesOrderAttachmentMissing(
    requireOnSalesOrder,
    uploadedAttachmentCount
  );

  useEffect(() => {
    return () => {
      const orderId = confirmCreateSalesOrderData()?.salesOrderId;
      if (!orderId) return;
      const { attachments, setAttachments } = useAttachmentStore.getState();
      setAttachments(attachments.filter((a) => a.contextId !== orderId));
    };
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      usePermitStore.getState().setBlockCompanybenefitInEdit('BUY_PERMIT');

      return () => {
        usePermitStore.getState().setBlockCompanybenefitInEdit('');
        usePermitStore.getState().setSelectedLicensePlatesPermitRedeem([]);
      };
    }, [])
  );

  const confirmCreatePermit = async () => {
    if (parkingSpaceNotSelected && reservationType === 'RESERVED') {
      return navigateToParkingSpaceViewer();
    }
    if (isUserRestricted) {
      useUiStore.getState().setSheetModal('accountRestricted');
    } else if (!selectedBillingFrequency) {
      useUiStore.getState().setSheetModal('billingIntervals');
    } else if (requiredAttachmentMissing) {
      useUiStore.getState().setSheetModal('addAttachment');
    } else {
      await createDraftSalesOrder({
        pmcId: selectedPmc?.pmcId ?? '',
        selectedPmc,
        startDate: new Date(
          getMidnightUTCFromISOString(startDate.toISOString())
        ),
        billingType,
        unlockCode,
        automaticOnlineOrderActivation: automaticOnlineOrderActivation ?? false,
        prices: prices ?? [],
        countryCode,
      });
    }
  };

  const navigateToParkingSpaceViewer = () => {
    return navigate('parkingSpaceMapViewer', {
      spotMap: parkZone?.spotMap ?? '',
      pmcId: params?.pmcId,
    });
  };

  useLayoutEffect(() => {
    if (freeSalesOrder) {
      setOptions({ title: t('button.redeem') });
    }
  }, [freeSalesOrder, setOptions, t]);

  const buyButtonText = useMemo((): string => {
    if (parkingSpaceNotSelected && reservationType === 'RESERVED') {
      return t('longTermParking.selectSpace');
    }
    if (freeSalesOrder) {
      return t('button.redeem');
    }
    if (!selectedBillingFrequency) {
      return t('longTermParking.selectBillingFrequency');
    }
    if (requiredAttachmentMissing && !isWeb) {
      return t('longTermParking.attachment.addAttachment');
    }
    return t('button.buyPermit');
  }, [
    parkingSpaceNotSelected,
    reservationType,
    selectedBillingFrequency,
    freeSalesOrder,
    requiredAttachmentMissing,
    t,
  ]);

  return (
    <BottomButtonsLayout
      contentBottom={
        <>
          {landlordName && (
            <StyledWrapper mt={10}>
              <Banner
                text={`${t('permit.transactionVendor')} ${landlordName}`}
                type="info"
                iconComponent={WarningIcon}
              />
            </StyledWrapper>
          )}
          <PermitBillingTypeInfoBox billingType={billingType} />
          <FooterButtonContainer
            pb={0}
            pt={0}
            style={{
              justifyContent: !showLicensePlate(selectedPmc)
                ? 'flex-end'
                : 'space-between',
            }}>
            {showLicensePlate(selectedPmc) && (
              <VehicleSelectDropDown
                allowMultipleSelection={
                  !!maxLicensePlatesPerQuantity &&
                  maxLicensePlatesPerQuantity > 1
                }
                handleVehiclesSelect={() =>
                  useUiStore.getState().setSheetModal('parkingVehicles')
                }
              />
            )}
            <PaymentCardDropDown isFreeSalesOrder={freeSalesOrder} />
          </FooterButtonContainer>
          {isMobilePaySelected && !freeSalesOrder && (
            <StyledWrapper mt={10}>
              <Banner
                text={t('paymentMethods.mobilePayNotSupportedForPermits')}
                type="warning"
                iconComponent={WarningIcon}
              />
            </StyledWrapper>
          )}
          {isWeb && requireOnSalesOrder && (
            <StyledWrapper mt={10}>
              <Banner
                text={t('longTermParking.attachment.webNotSupported')}
                type="warning"
                iconComponent={WarningIcon}
              />
            </StyledWrapper>
          )}
          {isMobilePaySelected && !freeSalesOrder ? (
            <Button
              size="l"
              onPress={() => addCard()}
              text={t('payment.addPaymentCard')}
              {...testIdentifiers('long-term-parking-add-card-button')}
              loading={loadingAddCard}
            />
          ) : (
            <Button
              disabled={
                (maxLicensePlatesPerQuantity === 1 &&
                  !licensePlate?.plateText) ||
                (maxLicensePlatesPerQuantity &&
                  maxLicensePlatesPerQuantity > 1 &&
                  selectedLicensePlates.length === 0) ||
                (!freeSalesOrder && !creditCardId) ||
                loadingSalesOrder ||
                hasInFlightUpload ||
                (isWeb && requiredAttachmentMissing)
              }
              size="l"
              onPress={confirmCreatePermit}
              text={buyButtonText}
              {...testIdentifiers('long-term-parking-buy-button')}
              loading={loadingSalesOrder || hasInFlightUpload}
            />
          )}
        </>
      }>
      <TitleCard
        parkingZoneName={placeName}
        price={price}
        location={undefined}
        permitName={name || ''}
        validFrom={startDate}
        changeValid={setSelectedPermitStartDate}
        testID="long-term-parking"
        isReserved={reservationType === 'RESERVED'}
        enableDateTimePicker={
          billingType &&
          ['CALENDAR', 'CALENDAR_FULL', 'ANNIVERSARY'].includes(billingType) &&
          allowStarDateChangeCountries[countryCode]
        }
        onParkingSpacePress={navigateToParkingSpaceViewer}
        onlineBookingFlexibleStartDate={onlineBookingFlexibleStartDate ?? 0}
        startSellingDate={startSellingDate}
        showAttachment={showOnSalesOrder && !!salesOrderId}
        promotion={promotion}
        promotionLoading={promotionLoading}
        country={countryCode}
        billingFrequency={selectedBillingFrequency}
      />
    </BottomButtonsLayout>
  );
};

export default BuyScreen;
